import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Access Denied Display Component
 * Shows 403 error when user doesn't have permission
 * Styled to match the "Failed to load" error UI for consistency
 */

interface AccessDeniedDisplayProps {
  message?: string;
  iconClass?: string;
  /** Page or resource name shown above the error so the user knows what they tried to access */
  title?: string;
  /** Where "Go Home" navigates. Defaults to '/'. */
  homePath?: string;
}

// Pull employee name + ULB (or state) name off localStorage.user_data. Used
// so a support screenshot carries enough identity for an admin to look the
// account up — nothing more.
function readUserContext(): { name?: string; ulbName?: string } {
  try {
    const raw = localStorage.getItem('user_data');
    if (!raw) return {};
    const u = JSON.parse(raw);
    // primaryBoundary.<type>.name is the human-readable boundary name (e.g.
    // "Tirupati Municipal Corporation" for ULB users, "Andhra Pradesh" for
    // state users). Falls back to tenantId when the boundary isn't populated.
    const pb = u?.primaryBoundary || {};
    const ulbName = pb.ulb?.name || pb.state?.name || pb.regional?.name || u?.tenantId || undefined;
    return { name: u?.name, ulbName };
  } catch {
    return {};
  }
}

export default function AccessDeniedDisplay({
  message = "This page isn't available with your current access.",
  iconClass = 'ri-lock-line',
  title,
  homePath = '/'
}: AccessDeniedDisplayProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const userCtx = readUserContext();

  return (
    <div className="min-h-full min-h-[calc(100vh-8rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Card */}
        <div className="relative bg-white dark:bg-gray-800/60 rounded-2xl shadow-xl ring-1 ring-gray-200 dark:ring-gray-700 px-8 py-10 text-center overflow-hidden">
          {/* Decorative gradient corner */}
          <div className="absolute -top-16 -right-16 w-48 h-48 bg-gradient-to-br from-red-100 to-purple-100 dark:from-red-900/20 dark:to-purple-900/20 rounded-full blur-2xl pointer-events-none" aria-hidden="true" />

          {/* 403 badge */}
          <div className="relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-[11px] font-semibold uppercase tracking-wider text-red-700 dark:text-red-300 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            403 · Forbidden
          </div>

          {/* Icon */}
          <div className="relative w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/40 dark:to-red-800/40 rounded-2xl rotate-3 flex items-center justify-center mx-auto mb-6 shadow-inner ring-1 ring-red-200/60 dark:ring-red-700/40">
            <i className={`${iconClass} text-4xl text-red-600 dark:text-red-400 -rotate-3`}></i>
          </div>

          {/* Optional resource title */}
          {title && (
            <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">
              {title}
            </p>
          )}

          {/* Heading */}
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">
            Access Denied
          </h3>

          {/* Message */}
          <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-2 leading-relaxed">
            {message}
          </p>

          {/* Sub-help */}
          <p className="text-sm text-gray-400 dark:text-gray-500 mb-8">
            Contact your administrator to request access.
          </p>

          {/* Attempted URL — small monospace pill for context */}
          {location?.pathname && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 font-mono max-w-full truncate mb-8">
              <i className="ri-link text-gray-400" aria-hidden="true" />
              <span className="truncate">{location.pathname}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
            >
              <i className="ri-arrow-left-line" aria-hidden="true" />
              Go Back
            </button>
            <button
              onClick={() => navigate(homePath)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-medium transition-colors"
            >
              <i className="ri-home-4-line" aria-hidden="true" />
              Go Home
            </button>
          </div>

          {/* Small identity line — shown so a support screenshot carries who
              was where. Deliberately quiet: same tone as the rest of the copy,
              no section heading, no border. */}
          {(userCtx.name || userCtx.ulbName) && (
            <p className="mt-6 text-xs text-gray-400 dark:text-gray-500">
              {[userCtx.name, userCtx.ulbName].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
